import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "@phosphor-icons/react";
import { KineticLines } from "../components/Reveal";
import { scrollToId } from "../lib/scroll";
import { useSiteImages } from "../context/SiteImagesContext";

const EASE = [0.22, 1, 0.36, 1];
const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.15, delayChildren: 0.9 } },
};
const item = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: EASE } },
};

export default function Hero() {
  const ref = useRef(null);
  const { resolve } = useSiteImages();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden pt-32 pb-20 lg:pt-24"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 w-full grid lg:grid-cols-12 gap-10 items-center">
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="lg:col-span-6 relative z-10"
        >
          <motion.p variants={item} className="overline mb-6">
            Carpeta organizadora médica · A4
          </motion.p>

          <h1 className="font-display text-[3.2rem] leading-[0.98] sm:text-6xl lg:text-7xl text-[#2D3732] font-medium">
            <KineticLines lines={["Tu salud y", "tu orden", "cuentan."]} delay={0.35} />
          </h1>

          <motion.p
            variants={item}
            className="text-base md:text-lg text-[#4A554E] max-w-md mt-7 leading-relaxed font-light"
          >
            La carpeta organizadora diseñada para ayudarte a tener toda tu documentación médica
            siempre localizada.
          </motion.p>

          <motion.div variants={item} className="flex flex-wrap items-center gap-4 mt-9">
            <button
              data-testid="hero-cta"
              onClick={() => scrollToId("colecciones")}
              className="group bg-[#A3B18A] hover:bg-[#8A9A6F] text-white rounded-full pl-7 pr-6 py-4 text-sm tracking-wide transition-colors duration-300 flex items-center gap-2"
            >
              Descubrir VITAE
              <ArrowRight
                size={17}
                className="group-hover:translate-x-1 transition-transform duration-300"
              />
            </button>
            <button
              onClick={() => scrollToId("historia")}
              className="text-sm text-[#2D3732] link-underline"
            >
              Nuestra historia
            </button>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-6 relative">
          <motion.div
            initial={{ opacity: 0, scale: 1.03 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, ease: EASE }}
            style={{ y }}
            className="relative rounded-[2rem] overflow-hidden bg-[#F6F1E7] p-3 shadow-[0_30px_80px_-30px_rgba(45,55,50,0.35)]"
          >
            <img
              src={resolve("hero", "")}
              alt="Las carpetas VITAE en distintos diseños, tamaños y colores"
              className="w-full h-auto object-contain rounded-[1.4rem]"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 0.9 }}
            className="absolute -bottom-6 -left-4 md:left-6 bg-white/95 backdrop-blur-md border border-[#EBEBEB] rounded-2xl px-6 py-4 shadow-sm max-w-[15rem]"
          >
            <p className="font-display text-xl text-[#2D3732] leading-tight">
              Varios diseños, tamaños y colores
            </p>
            <p className="overline text-[0.6rem] mt-1">Elige la tuya</p>
          </motion.div>
        </div>
      </div>

      <button
        onClick={() => scrollToId("historia")}
        className="hidden lg:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-[#6B705C]"
      >
        <span className="overline text-[0.55rem]">Descubre</span>
        <ArrowDown size={18} className="animate-bounce" />
      </button>
    </section>
  );
}
